import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Settings } from 'luxon';
import { JxtRecurrenceModal } from '@jooxter/ui';
import {
  BookingRecurrence,
  IBookingForm,
  now,
  useLatest,
  useBookingRecurrenceToRruleOptionsAdapter,
  useGenerateRrule,
  useGenerateRruleLabel,
} from '@jooxter/utils';
import { JxtRecurrenceSelect } from '../../../../components';

/** Recurrence picker and its "customize" modal. Only rendered when the resource allows recurrence. */
export const JxtBookingRecurrenceField = () => {
  const { control, setValue } = useFormContext<IBookingForm>();
  const watchResource = useWatch({ control, name: 'resource' });
  const watchStart = useWatch({ control, name: 'start' });
  const watchRrule = useWatch({ control, name: 'rrule' });
  const resourceTimezone = watchResource?.timezone ? watchResource.timezone : Settings.defaultZone.name;
  const startDay = watchStart.date.toISODate();

  const { adapt, revert } = useBookingRecurrenceToRruleOptionsAdapter(watchStart.date);
  const { generate: generateRrule } = useGenerateRrule(watchStart.date, resourceTimezone);
  const { rruleLabel } = useGenerateRruleLabel(watchStart.date, watchRrule);

  const [showModal, setShowModal] = useState(false);
  // The recurrence the user picked, kept as the single source of truth. It cannot be read back from
  // the form's `rrule`: a generated rrule bakes in the weekday of the start date, so once the
  // booking moves to another day `revert` can no longer tell "every Monday" from a custom rule.
  const [recurrence, setRecurrence] = useState<BookingRecurrence>(() => revert(watchRrule ?? undefined));
  const hasSeededRrule = useRef(false);
  // `adapt` and `generateRrule` are rebuilt from the start date on every render; the effect below
  // must read the current ones without being re-keyed on their identity.
  const latest = useLatest({ adapt, generateRrule, setValue });

  // The rrule is derived from the choice above and the start date - never kept in sync afterwards -
  // so moving the booking to another day regenerates it ("every Monday" follows to "every Tuesday")
  // instead of leaving a stale rule behind. Skipped on mount so a booking being edited keeps the
  // exact rrule it was created with, and skipped for a custom rule, which the modal authors.
  useEffect(() => {
    if (!hasSeededRrule.current) {
      hasSeededRrule.current = true;
      return;
    }

    if (recurrence === BookingRecurrence.Customize) {
      return;
    }

    latest.current.setValue(
      'rrule',
      latest.current.generateRrule(latest.current.adapt(recurrence))?.toString().split('RRULE:')[1] ?? null
    );
  }, [startDay, recurrence, latest]);

  const onRecurrenceSelect = (value: BookingRecurrence) => {
    // A custom rule is only committed once the modal is confirmed, so cancelling leaves the picker
    // on the previous choice.
    if (value === BookingRecurrence.Customize) {
      setShowModal(true);
      return;
    }

    setRecurrence(value);
  };

  const onCloseModal = (value?: string) => {
    if (value) {
      setRecurrence(BookingRecurrence.Customize);
      setValue('rrule', value);
    }

    setShowModal(false);
  };

  return (
    <>
      <div className="flex items-center gap-3">
        <i className="fas fa-fw fa-lg fa-arrows-rotate shrink-0 text-neutral-60" />
        <JxtRecurrenceSelect
          className="grow"
          date={watchStart.date}
          value={recurrence}
          onRecurrenceSelect={onRecurrenceSelect}
          customizeLabel={recurrence === BookingRecurrence.Customize ? rruleLabel : undefined}
        />
      </div>
      <JxtRecurrenceModal
        date={watchStart?.date || now(resourceTimezone)}
        show={showModal}
        value={watchRrule}
        onHide={onCloseModal}
      />
    </>
  );
};

export default JxtBookingRecurrenceField;
